/**
 * Step1PersonaCard
 * Step 1 of the plan workflow - Select target personas
 * Includes inline "Add Persona" capability with Agent A + B validation
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import {
  Users, Check, Plus, Pencil, Loader2, AlertTriangle,
  Sparkles, Target, Lightbulb, User, RefreshCw, X, AlertCircle
} from 'lucide-react';
import { plansApi } from '../api/plansApi';

const CONFIDENCE_COLORS = {
  very_high: 'bg-green-500/10 text-green-600 border-green-200',
  high: 'bg-blue-500/10 text-blue-600 border-blue-200',
  medium: 'bg-amber-500/10 text-amber-600 border-amber-200',
  low: 'bg-gray-500/10 text-gray-600 border-gray-200'
};

export default function Step1PersonaCard({ planId, stepData, isCompleted, onSave, onEdit, saving }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [assetPersonas, setAssetPersonas] = useState([]);
  const [suggestedPersonas, setSuggestedPersonas] = useState([]);
  const [selectedIds, setSelectedIds] = useState(stepData?.selected_persona_ids || []);
  const [planPersonas, setPlanPersonas] = useState(stepData?.plan_personas || []);
  
  // Add Persona inline state
  const [showAddPersona, setShowAddPersona] = useState(false);
  const [addPersonaText, setAddPersonaText] = useState('');
  const [addingPersona, setAddingPersona] = useState(false);
  const [addError, setAddError] = useState(null);
  const [overlapData, setOverlapData] = useState(null);

  // Helper to get persona ID
  const getPersonaId = (p) => p.persona_id || p.id || `temp_${p.name}`;

  useEffect(() => {
    if (!isCompleted) {
      loadData(false);
    } else {
      setLoading(false);
    }
  }, [planId, isCompleted]);

  const loadData = async (forceRefresh = false) => {
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const data = await plansApi.getPersonaSuggestions(planId, forceRefresh);
      const fetchedAssetPersonas = data.asset_personas || [];
      setAssetPersonas(fetchedAssetPersonas);
      setSuggestedPersonas(data.suggested_personas || []);
      
      // Merge asset personas into planPersonas if not already present
      if (fetchedAssetPersonas.length > 0) {
        setPlanPersonas(prev => {
          const existingIds = new Set(prev.map(p => getPersonaId(p)));
          const newAssetPersonas = fetchedAssetPersonas.filter(ap => !existingIds.has(getPersonaId(ap)));
          if (newAssetPersonas.length > 0) {
            return [...newAssetPersonas, ...prev];
          }
          return prev;
        });
      }
      
      if (forceRefresh) {
        toast.success('Personas refreshed');
      }
    } catch (error) {
      console.error('Failed to load personas:', error);
      toast.error('Failed to load personas');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const toggleSelection = (personaId) => {
    if (selectedIds.includes(personaId)) {
      setSelectedIds(selectedIds.filter(id => id !== personaId));
    } else {
      setSelectedIds([...selectedIds, personaId]);
    }
  };

  const addSuggested = (suggestion) => {
    const p = suggestion.persona;
    if (!planPersonas.find(x => getPersonaId(x) === p.persona_id)) {
      setPlanPersonas([...planPersonas, p]);
      setSelectedIds([...selectedIds, p.persona_id]);
      toast.success(`Added ${p.name}`);
    }
  };

  // Handle Add Persona submission
  const handleAddPersona = async () => {
    if (!addPersonaText.trim()) return;
    
    setAddingPersona(true);
    setAddError(null);
    setOverlapData(null);
    
    try {
      const result = await plansApi.addPersona(planId, addPersonaText);
      
      if (result.status === 'ok') {
        // Success - add persona to list and auto-select
        const newPersona = result.persona;
        setPlanPersonas([...planPersonas, newPersona]);
        setSelectedIds([...selectedIds, newPersona.persona_id]);
        setAddPersonaText('');
        setShowAddPersona(false);
        toast.success(`Added: ${newPersona.name}`);
      } else if (result.status === 'overlap') {
        // Overlap detected - show resolution UI
        setOverlapData({
          message: result.message,
          suggestedExistingId: result.suggested_existing_persona_id,
          reason: result.overlap_reason,
          candidatePersona: result.candidate_persona
        });
      } else if (result.status === 'error' || result.status === 'invalid') {
        // Error or invalid input
        setAddError({
          message: result.message || 'Failed to create persona',
          help: result.help || ''
        });
      }
    } catch (error) {
      console.error('Failed to add persona:', error);
      setAddError({
        message: 'Failed to create persona',
        help: 'Please try again'
      });
    } finally {
      setAddingPersona(false);
    }
  };

  // Handle "Use Existing" for overlap
  const handleUseExisting = () => {
    if (overlapData?.suggestedExistingId) {
      // Find and select the existing persona
      const existingPersona = planPersonas.find(p => 
        getPersonaId(p) === overlapData.suggestedExistingId
      );
      if (existingPersona && !selectedIds.includes(getPersonaId(existingPersona))) {
        setSelectedIds([...selectedIds, getPersonaId(existingPersona)]);
        toast.success(`Selected existing persona: ${existingPersona.name}`);
      }
    }
    setOverlapData(null);
    setAddPersonaText('');
    setShowAddPersona(false);
  };

  // Handle "Keep Separate Anyway" for overlap
  const handleKeepSeparate = async () => {
    if (!overlapData?.candidatePersona) return;
    
    setAddingPersona(true);
    try {
      const result = await plansApi.addPersonaForce(planId, overlapData.candidatePersona);
      
      if (result.status === 'ok') {
        const newPersona = result.persona;
        setPlanPersonas([...planPersonas, newPersona]);
        setSelectedIds([...selectedIds, newPersona.persona_id]);
        setAddPersonaText('');
        setShowAddPersona(false);
        setOverlapData(null);
        toast.success(`Added: ${newPersona.name} (confidence adjusted)`);
      }
    } catch (error) {
      console.error('Failed to force-add persona:', error);
      toast.error('Failed to add persona');
    } finally {
      setAddingPersona(false);
    }
  };

  // Cancel add persona
  const handleCancelAdd = () => {
    setShowAddPersona(false);
    setAddPersonaText('');
    setAddError(null);
    setOverlapData(null);
  };

  const handleSave = () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one persona');
      return;
    }
    const selected = planPersonas.filter(p => selectedIds.includes(getPersonaId(p)));
    onSave({ selected_persona_ids: selectedIds, plan_personas: selected });
  };

  // Completed view
  if (isCompleted) {
    const selected = stepData?.plan_personas || [];
    return (
      <div className="bg-surface border border-green-200 dark:border-green-800 rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
              <Check className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Step 1 — Target Personas Selected</h3>
              <div className="flex flex-wrap gap-2 mt-2">
                {selected.map(p => (
                  <Badge key={getPersonaId(p)} variant="secondary">
                    <Users className="w-3 h-3 mr-1" />{p.name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="w-4 h-4 mr-1" />Edit
          </Button>
        </div>
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="bg-surface border border-purple-200 dark:border-purple-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-purple-500" />
          </div>
          <h3 className="font-semibold">Step 1 — Pick Your Target Personas</h3>
        </div>
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </div>
    );
  }

  // Active state
  return (
    <div className="bg-surface border border-purple-200 dark:border-purple-800 rounded-xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h3 className="font-semibold">Step 1 — Pick Your Target Personas</h3>
            <p className="text-sm text-muted-foreground">
              Select 1–2 primary personas (recommended). Focus improves campaign performance.
            </p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => loadData(true)}
          disabled={refreshing}
          className="text-muted-foreground"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {selectedIds.length > 2 && (
        <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-200 rounded-lg mb-4">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span className="text-sm text-amber-700">Consider focusing on 1–2 personas.</span>
        </div>
      )}

      <div className="space-y-6">
        {/* Asset Personas */}
        {planPersonas.length > 0 && (
          <div>
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-purple-500" />Personas from your website
            </h4>
            <div className="space-y-2">
              {planPersonas.map(p => {
                const pid = getPersonaId(p);
                const isSelected = selectedIds.includes(pid);
                return (
                  <div 
                    key={pid}
                    onClick={() => toggleSelection(pid)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      isSelected ? 'border-purple-500 bg-purple-500/5' : 'border-border hover:border-muted-foreground/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                        isSelected ? 'bg-purple-500 border-purple-500' : 'border-muted-foreground/30'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{p.name}</span>
                          {p.confidence && (
                            <Badge variant="outline" className={`text-xs ${CONFIDENCE_COLORS[p.confidence] || ''}`}>
                              {p.confidence.replace('_', ' ')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{p.role_or_identity || p.role}</p>
                        {p.core_intent && (
                          <p className="text-sm mt-1"><span className="font-medium">Intent:</span> {p.core_intent}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Suggested Personas */}
        {suggestedPersonas.length > 0 && (
          <div>
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-amber-500" />Additional Personas to Consider
            </h4>
            <div className="space-y-2">
              {suggestedPersonas.map((s, idx) => {
                const p = s.persona;
                const alreadyAdded = planPersonas.some(x => getPersonaId(x) === p?.persona_id);
                return (
                  <div key={p?.persona_id || idx} className="p-4 border border-amber-200 bg-amber-500/5 rounded-lg">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-amber-600" />
                          <span className="font-medium">{p?.name}</span>
                          {p?.confidence && (
                            <Badge variant="outline" className={`text-xs ${CONFIDENCE_COLORS[p.confidence] || ''}`}>
                              {p.confidence.replace('_', ' ')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{p?.role_or_identity}</p>
                        {s.why_recommended && (
                          <p className="text-sm text-amber-700 mt-2 italic">"{s.why_recommended}"</p>
                        )}
                      </div>
                      <Button size="sm" onClick={() => addSuggested(s)} disabled={alreadyAdded}
                        className={alreadyAdded ? 'bg-green-500' : 'bg-amber-500 hover:bg-amber-600'}>
                        {alreadyAdded ? <><Check className="w-4 h-4 mr-1" />Added</> : <><Plus className="w-4 h-4 mr-1" />Add</>}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Add Persona Inline */}
        <div className="pt-2">
          {!showAddPersona ? (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowAddPersona(true)}
              className="w-full border-dashed"
              data-testid="add-persona-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Persona
            </Button>
          ) : (
            <div className="border border-purple-200 dark:border-purple-800 rounded-lg p-4 space-y-4 bg-purple-500/5">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  Add Strategic Persona
                </h4>
                <Button variant="ghost" size="sm" onClick={handleCancelAdd} className="h-6 w-6 p-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <Textarea
                value={addPersonaText}
                onChange={(e) => setAddPersonaText(e.target.value)}
                placeholder="Describe a target role, intent, and key pain points"
                className="min-h-[80px] resize-none"
                disabled={addingPersona}
                data-testid="add-persona-textarea"
              />
              
              {/* Error display */}
              {addError && (
                <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <p className="text-red-700 font-medium">{addError.message}</p>
                    {addError.help && <p className="text-red-600 mt-1">{addError.help}</p>}
                  </div>
                </div>
              )}
              
              {/* Overlap resolution UI */}
              {overlapData && (
                <div className="p-3 bg-amber-500/10 border border-amber-200 rounded-lg space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <p className="text-amber-800 font-medium">{overlapData.message}</p>
                      {overlapData.reason && (
                        <p className="text-amber-700 mt-1">{overlapData.reason}</p>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-amber-700">
                    Would you like to use the existing persona instead?
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={handleUseExisting}
                      className="bg-amber-500 hover:bg-amber-600"
                    >
                      Use Existing
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={handleKeepSeparate}
                      disabled={addingPersona}
                    >
                      {addingPersona ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : null}
                      Keep Separate Anyway
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Generate button */}
              {!overlapData && (
                <div className="flex justify-end">
                  <Button 
                    size="sm" 
                    onClick={handleAddPersona} 
                    disabled={addingPersona || !addPersonaText.trim()}
                    className="bg-purple-500 hover:bg-purple-600"
                    data-testid="generate-persona-btn"
                  >
                    {addingPersona ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Generate Persona
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Save */}
        <div className="pt-4 border-t flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{selectedIds.length} selected</span>
          <Button onClick={handleSave} disabled={selectedIds.length === 0 || saving}
            className="bg-purple-500 hover:bg-purple-600 text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Confirm Personas
          </Button>
        </div>
      </div>
    </div>
  );
}
